import Joi from 'joi';
import { Stack, Button, VStack, Link as ExternalLink, Text, Input } from '@chakra-ui/react';
import { AutocompleteField } from 'components/common/Form';
import { passwordRegex } from 'config/regex';
import { useRegister } from 'utils/auth';
import { AuthenticationType } from '@novaheal/types';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { axios } from 'utils/axios';
import { toastContent } from 'config/toast';
import { ReactElement, useState } from 'react';
import { CancelIcon, LanguageIcon, UserIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { FieldError } from 'react-hook-form';
import { RegisterStepProps } from 'pages/auth/Registration';
import { ExternalRoutes } from 'types/Routes';
import { PasswordInputField } from 'components/common/Form/PasswordInputField';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { useCountryApi } from 'hooks/api/backend/useCountryApi';
import { RegisterWithCountry } from 'utils/auth/local/local-auth';
import { logFirebaseUserFlow } from 'utils/firebaseLogs';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { handleMutationError } from 'utils/handleMutationError';
import { RegistrationLayout } from './RegistrationLayout';
import { EmailStatusType } from '../../types/StatusTypes';
import { Form, RHFCheckboxField, RHFFormInput } from '../common/Form/v2';

const schema = Joi.object({
  referral: Joi.string().optional(),
  email: Joi.string()
    .email({
      tlds: {
        allow: false,
      },
    })
    .required()
    .messages({
      'string.empty': errorMessage.empty,
      'string.email': errorMessage.email,
    }),
  password: Joi.string().min(8).pattern(new RegExp(passwordRegex)).required().messages({
    'string.min': errorMessage.pwLength,
    'string.empty': errorMessage.empty,
    'string.pattern.base': errorMessage.pwRequirements,
  }),
  firstName: Joi.string().required().messages({
    'string.empty': errorMessage.empty,
  }),
  lastName: Joi.string().required().messages({
    'string.empty': errorMessage.empty,
  }),
  acceptedTerms: Joi.boolean().required().invalid(false).messages({
    'any.invalid': errorMessage.checkboxRequired,
  }),
  marketingEmailOptIn: Joi.boolean(),
  countryId: Joi.string().required().messages({
    '*': 'Du musst ein Land aus der Liste auswählen',
    'any.required': 'Du musst ein Land aus der Liste auswählen',
    'string.empty': 'Du musst ein Land aus der Liste auswählen',
  }),
  country: Joi.string().required().messages({
    '*': 'Du musst ein Land aus der Liste auswählen',
    'any.required': 'Du musst ein Land aus der Liste auswählen',
    'string.empty': 'Du musst ein Land aus der Liste auswählen',
  }),
});

export const RegisterStep2 = ({
  prevStep,
  nextStep,
  handleChanges,
  values,
}: RegisterStepProps): ReactElement => {
  const register = useRegister();
  const toast = useNovaToast();
  const [registerButtonDisabled, setRegisterButtonDisabled] = useState(false);
  const { getAutoCompleteItems } = useCountryApi();

  const handleSubmit = async (formValues: RegisterWithCountry): Promise<void> => {
    setRegisterButtonDisabled(true);
    const { email, acceptedTerms, ...rest } = formValues;

    try {
      const validateMail = await axios.post('/auth/validate', {
        email,
      });

      if (validateMail.data === 'USED') {
        toast.show(toastContent.email[EmailStatusType.TAKEN]);
        setRegisterButtonDisabled(false);
      } else {
        const user = await register.mutateAsync({
          ...rest,
          email,
          acceptedTerms,
          authenticationType: AuthenticationType.EMAIL,
        });
        logFirebaseUserFlow('registration', {
          ourUserId: user?.id,
        });

        setRegisterButtonDisabled(false);
        nextStep();
      }
    } catch (error) {
      handleMutationError(error);
    } finally {
      setRegisterButtonDisabled(false);
    }
  };
  return (
    <RegistrationLayout backButtonFn={prevStep}>
      <Stack width="100%">
        <Form<RegisterWithCountry, typeof schema>
          onSubmit={handleSubmit}
          schema={schema}
          autoComplete="off"
          options={{
            defaultValues: {
              email: values.email,
              password: values.password,
              firstName: values.firstName,
              lastName: values.lastName,
              acceptedTerms: values.acceptedTerms,
              marketingEmailOptIn: values.marketingEmailOptIn,
              countryId: '',
              country: '',
            },
          }}
        >
          {({ register, formState, watch, resetField, setValue, getValues }) => {
            return (
              <>
                <RHFFormInput
                  name="firstName"
                  placeholder="Vorname"
                  leftElement={
                    <NovaIcon icon={UserIcon} size="s" aria-hidden color="icon.tertiary" />
                  }
                  clearable
                />
                <RHFFormInput
                  name="lastName"
                  placeholder="Nachname"
                  leftElement={
                    <NovaIcon icon={UserIcon} size="s" aria-hidden color="icon.tertiary" />
                  }
                  clearable
                />

                <input type="hidden" value={values.email} {...register('email')} />
                {/* hidden input to catch chromes autofill behavior, as it skips autoComplete="off|false" */}
                <Input display="none" />
                <PasswordInputField
                  placeholder="Passwort"
                  autoComplete="new-password"
                  error={formState.errors.password as FieldError}
                  register={() =>
                    register('password', {
                      onChange: handleChanges('password'),
                      value: values.password,
                    })
                  }
                  value={watch('password')}
                  reset={() => resetField('password')}
                />

                <AutocompleteField
                  getAutocompleteItems={getAutoCompleteItems}
                  initialName={undefined}
                  initialId={undefined}
                  registrationName="country"
                  error={formState.errors.countryId as FieldError}
                  idName="countryId"
                  disableCreate
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                  placeholder="Land oder Region"
                  leftElement={
                    <NovaIcon
                      icon={LanguageIcon}
                      size="s"
                      color={watch('country') ? 'icon.accent' : 'icon.quaternary'}
                      aria-hidden
                    />
                  }
                  zIndex={1}
                  rightElement={
                    watch('country') ? (
                      <NovaIconButton
                        variant="primary"
                        size="s"
                        bg="transparent"
                        onClick={() => {
                          resetField('country');
                          resetField('countryId');
                        }}
                        icon={CancelIcon}
                        color="icon.tertiary"
                      />
                    ) : undefined
                  }
                />

                <VStack align="left" pt="5">
                  <RHFCheckboxField<RegisterWithCountry>
                    name="acceptedTerms"
                    labelElement={
                      <Text textStyle="body.s.regular">
                        Hiermit bestätige ich, dass ich die{' '}
                        <ExternalLink
                          color="text.info"
                          href={ExternalRoutes.TERMS}
                          target="_blank"
                          textStyle="body.s.regular"
                        >
                          AGBs
                        </ExternalLink>{' '}
                        und die{' '}
                        <ExternalLink
                          color="text.info"
                          href={ExternalRoutes.PRIVACY}
                          target="_blank"
                          textStyle="body.s.regular"
                        >
                          Datenschutzerklärung
                        </ExternalLink>{' '}
                        gelesen habe und akzeptiere.
                        <Text as="span" textStyle="body.s.regular" color="text.error">
                          {' '}
                          *
                        </Text>
                      </Text>
                    }
                  />
                  <RHFCheckboxField<RegisterWithCountry>
                    name="marketingEmailOptIn"
                    labelElement={
                      <Text textStyle="body.s.regular">
                        Ja, ich möchte von NOVAHEAL per E-Mail über Neuigkeiten, Angebote,
                        Produktupdates und Feedbackanfragen informiert werden. Die Einwilligung kann
                        ich jederzeit in meinen Profileinstellungen oder per E-Mail widerrufen.
                        Weitere Informationen in unserer{' '}
                        <ExternalLink
                          color="text.info"
                          href={ExternalRoutes.PRIVACY}
                          target="_blank"
                          textStyle="body.s.regular"
                        >
                          Datenschutzerklärung
                        </ExternalLink>
                        .
                      </Text>
                    }
                  />
                </VStack>
                <Stack align="center" pt="10">
                  <Button
                    variant="primary"
                    type="submit"
                    w="full"
                    disabled={
                      registerButtonDisabled || !formState.isValid || !watch('acceptedTerms')
                    }
                  >
                    Registrieren
                  </Button>
                  <Stack direction="row" align="center" gap="3xs">
                    <Text textStyle="body.s.regular" color="text.error">
                      *
                    </Text>
                    <Text textStyle="body.s.regular" color="text.tertiary">
                      Pflichtfeld
                    </Text>
                  </Stack>
                </Stack>
              </>
            );
          }}
        </Form>
      </Stack>
    </RegistrationLayout>
  );
};
